import { SearchX } from 'lucide-react';
import type { ReactNode } from 'react';

import { PageHeader, type PageHeaderProps } from '~/components/layout/PageHeader';
import { PageShell } from '~/components/layout/PageShell';
import {
  Button,
  Card,
  CardBody,
  ConfirmModal,
  EmptyState,
  LoadError,
  SkeletonFields,
} from '~/components/ui';
import type { UnsavedGuard } from '~/hooks/useUnsavedGuard';

export interface RecordPageShellProps
  extends Pick<PageHeaderProps, 'title' | 'description' | 'breadcrumbs' | 'actions'> {
  /** The record is still loading: field skeletons in place of the children. */
  loading?: boolean;
  /** The load failed: a `LoadError` in place. `what` reads "the persona"; `message` comes from `apiErrorMessage()`. */
  error?: { what: string; message?: string } | null;
  onRetry?: () => void;
  /** The record does not exist (404, or an id the list never had). */
  notFound?: boolean;
  /** "Persona not found". */
  notFoundTitle?: string;
  /** The list the not-found state links back to. */
  backTo?: string;
  backLabel?: string;
  /** From `useUnsavedGuard`: renders the leave-this-page confirmation. */
  guard?: UnsavedGuard;
  testId?: string;
  children?: ReactNode;
}

/**
 * A record page (create or edit): `PageShell` + `PageHeader`, then the record's load state in place
 * (not found, error, loading) or the page's own `<form>` cards (DESIGN.md §1.6).
 */
export function RecordPageShell({
  loading,
  error,
  onRetry,
  notFound,
  notFoundTitle = 'Not found',
  backTo,
  backLabel = 'Back',
  guard,
  testId,
  children,
  ...header
}: RecordPageShellProps) {
  let body = children;
  if (notFound) {
    body = (
      <Card>
        <CardBody>
          <EmptyState
            icon={<SearchX className="size-8" aria-hidden="true" />}
            title={notFoundTitle}
            description="It may have been deleted, or the link may be wrong."
            action={
              backTo && (
                <Button variant="outline" href={backTo}>
                  {backLabel}
                </Button>
              )
            }
          />
        </CardBody>
      </Card>
    );
  } else if (error) {
    body = (
      <LoadError what={error.what} message={error.message} onRetry={onRetry} testId={testId} />
    );
  } else if (loading) {
    body = (
      <Card>
        <CardBody>
          <SkeletonFields fields={6} />
        </CardBody>
      </Card>
    );
  }

  return (
    <PageShell>
      <PageHeader {...header} />
      {body}
      {guard && (
        <ConfirmModal
          isOpen={guard.blocked}
          title="Discard unsaved changes?"
          message={guard.message}
          confirmLabel="Discard changes"
          testId="unsaved-guard"
          onConfirm={guard.leave}
          onClose={guard.stay}
        />
      )}
    </PageShell>
  );
}
